// =============================================================================
// SimSense — Wishlist / Saved Setups Hook (OT-297)
// =============================================================================
// localStorage-backed wishlist. Will migrate to API when backend lands.

import { useState, useCallback, useEffect } from "react";

const STORAGE_KEY = "ss_wishlist";

function loadWishlist(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
}

function saveWishlist(ids: Set<string>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
}

export function useWishlist() {
  const [savedIds, setSavedIds] = useState<Set<string>>(loadWishlist);

  // Sync across tabs
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setSavedIds(loadWishlist());
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const isSaved = useCallback((id: string) => savedIds.has(id), [savedIds]);

  const toggleSave = useCallback((id: string) => {
    setSavedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveWishlist(next);
      return next;
    });
  }, []);

  const savedCount = savedIds.size;

  return { isSaved, toggleSave, savedIds, savedCount };
}
